import type { SyncStatus } from '@matter_extractor/shared';

/**
 * What the banner of the atelier and the three boxes of « État de la prod » SAY (vitrine.md §9.3,
 * lot F of issue #192; maquette `vitrine/atelier.html`, sections 1 and 2) - pure, so every text and
 * every state is tested without a router, a server or a clock.
 *
 * The state comes from `GET /api/atelier/sync` (`SyncStatus`), the code of the dev from the instance
 * the shell read (`useInstanceContext().code`), the clock from the caller: nothing here reads
 * `Date.now()`.
 *
 * ## A gap to the maquette, and why
 *
 * The maquette says « Prod : v1.3.0 du 6 oct. » and « 12 commits d'avance ». Neither the date of the
 * tag nor the number of commits is known to the server: the image carries no git history, and
 * `GET /api/instance` of the prod says a code - no date, no SHA. So the banner says the code, without
 * a date, and « voir l'avance » opens the GitLab comparison of the two codes, which gives the exact
 * advance on the click. A count the server would have to invent is worse than a link that is right.
 */

/** The comparison of two refs of the repository, on the GitLab of the team. */
export const GITLAB_COMPARE_BASE =
  'https://gitlab.patrickmathieu.net/pms-digital/matter-extractor/-/compare';

/** How long after a click that went through the line says « Synchronisé à l’instant : ... ». */
export const JUST_RAN_MS = 60_000;

/** The code a workstation reports (`instanceCode`): no tag, no commit. */
export const LOCAL_CODE = 'local';

/** `HH:MM`, local time - « 13:00 ». An unreadable stamp gives an empty string. */
export function clockTime(stamp: string): string {
  const at = new Date(stamp);
  if (Number.isNaN(at.getTime())) return '';
  const two = (value: number) => String(value).padStart(2, '0');
  return `${two(at.getHours())}:${two(at.getMinutes())}`;
}

/**
 * How long ago, in French: « à l’instant » under a minute, « il y a 23 min », « il y a 2 h »,
 * « il y a 3 j ». A stamp in the future (two clocks that disagree) is « à l’instant » too.
 */
export function relativeFrench(stamp: string, now: Date): string {
  const then = new Date(stamp).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.floor((now.getTime() - then) / 60_000);
  if (minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  return `il y a ${Math.floor(hours / 24)} j`;
}

/**
 * The code of the dev as the banner writes it: a short SHA is `main@b44e9b8` (the image of `main`),
 * a tag stays itself (`v1.4.0`), a workstation stays `local`.
 */
export function devCodeLabel(code: string): string {
  if (/^v\d/.test(code)) return code;
  if (/^[0-9a-f]{7,40}$/i.test(code)) return `main@${code}`;
  return code;
}

/**
 * The GitLab comparison from the code of the prod to the code of the dev - « voir l'avance » - or
 * `null` when there is nothing to compare: a code missing, one that is `local` (a workstation has no
 * ref on the remote), or the two codes equal (an empty comparison would promise an advance that is
 * not there).
 */
export function compareHref(prodCode: string | null, devCode: string | null): string | null {
  if (prodCode === null || devCode === null) return null;
  if (prodCode === '' || devCode === '') return null;
  if (prodCode === LOCAL_CODE || devCode === LOCAL_CODE) return null;
  if (prodCode === devCode) return null;
  return `${GITLAB_COMPARE_BASE}/${encodeURIComponent(prodCode)}...${encodeURIComponent(devCode)}`;
}

/** Whether the last synchronisation failed: an error newer than the last success (or no success at all). */
export function isFailing(status: Pick<SyncStatus, 'lastSuccessAt' | 'lastErrorAt'>): boolean {
  if (status.lastErrorAt === null) return false;
  if (status.lastSuccessAt === null) return true;
  return Date.parse(status.lastErrorAt) > Date.parse(status.lastSuccessAt);
}

/** What the last click that went through said, kept by the banner: the sentence and the instant. */
export type JustRan = { readonly summary: string; readonly at: number };

export type BannerView = {
  /** Red: the last error is newer than the last success. */
  failed: boolean;
  /** A run is on: the spinner, and « Synchroniser » inert. */
  running: boolean;
  /** « Prod : v1.3.0 », « Prod : v1.3.0 (lu à 13:00) », « Prod : code inconnu ». */
  prodText: string;
  /** « Dev : main@b44e9b8 ». */
  devText: string;
  /** « voir l'avance » target, or null. */
  compareHref: string | null;
  /** The status line. */
  stateText: string;
};

/** The banner of the atelier, as a plain value (maquette section 1, variant A). */
export function bannerView(input: {
  status: SyncStatus;
  devCode: string;
  now: Date;
  justRan?: JustRan | null;
}): BannerView {
  const { status, devCode, now } = input;
  const failed = isFailing(status);
  const prod = status.prod;

  // « (lu à 13:00) » only when a failure came AFTER the reading: the code may be older than the
  // failure, and the banner says since when it knows it.
  const readBeforeFailure =
    failed && prod !== null && Date.parse(status.lastErrorAt ?? '') > Date.parse(prod.readAt);
  const prodText =
    prod === null
      ? 'Prod : code inconnu'
      : readBeforeFailure
        ? `Prod : ${prod.code} (lu à ${clockTime(prod.readAt)})`
        : `Prod : ${prod.code}`;

  return {
    failed,
    running: status.running,
    prodText,
    devText: `Dev : ${devCodeLabel(devCode)}`,
    compareHref: compareHref(prod?.code ?? null, devCode),
    stateText: stateLine(status, failed, now, input.justRan ?? null),
  };
}

function stateLine(
  status: SyncStatus,
  failed: boolean,
  now: Date,
  justRan: JustRan | null,
): string {
  if (status.running) return 'Synchronisation…';
  if (failed) {
    const error = status.lastError ?? 'raison inconnue';
    const success =
      status.lastSuccessAt === null
        ? 'jamais réussie'
        : `dernière réussie à ${clockTime(status.lastSuccessAt)}`;
    return `Dernière synchronisation échouée à ${clockTime(status.lastErrorAt ?? '')} : ${error} · ${success}`;
  }
  if (justRan !== null && now.getTime() - justRan.at < JUST_RAN_MS) {
    return `Synchronisé à l’instant : ${justRan.summary}`;
  }
  if (status.lastSuccessAt !== null)
    return `Synchronisé ${relativeFrench(status.lastSuccessAt, now)}`;
  return 'Jamais synchronisé';
}

/** The box « Synchronisation » of the screen: the value, and whether it is a failure. */
export function syncBox(status: SyncStatus, now: Date): { text: string; bad: boolean } {
  if (status.running) return { text: 'en cours…', bad: false };
  if (isFailing(status))
    return { text: `échec à ${clockTime(status.lastErrorAt ?? '')}`, bad: true };
  if (status.lastSuccessAt !== null) {
    return { text: relativeFrench(status.lastSuccessAt, now), bad: false };
  }
  return { text: 'jamais', bad: false };
}

/** The line under the code of the prod: when it was read. */
export function prodReadNote(status: SyncStatus): string {
  return status.prod === null
    ? 'jamais lu : la prod n’a pas répondu à /api/instance'
    : `lu à ${clockTime(status.prod.readAt)}`;
}

/** The line under the code of the dev: the comparison, or why there is none. */
export function devCompareNote(
  prodCode: string | null,
  devCode: string,
): { href: string } | { text: string } {
  const href = compareHref(prodCode, devCode);
  if (href !== null) return { href };
  if (devCode === LOCAL_CODE) return { text: 'poste local : pas de comparaison' };
  if (prodCode === null) return { text: 'code de la prod inconnu : pas de comparaison' };
  if (prodCode === devCode) return { text: 'le même code que la prod' };
  return { text: 'pas de comparaison' };
}
